import * as React from "react";
import {
  Activity,
  BarChart3,
  CalendarClock,
  ClipboardCheck,
  Coins,
  FileText,
  Gauge,
  History,
  KeyRound,
  LayoutGrid,
  ListChecks,
  LogOut,
  Mail,
  MessageSquare,
  Phone,
  Send,
  Settings,
  ShieldCheck,
  Snowflake,
  Users,
  Zap,
} from "lucide-react";

import { AROCIcon } from "@/components/AROCIcon";
import { NavLink, Outlet, useLocation } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { LicenseBanner } from "@/components/LicenseBanner";
import { ThemeToggle } from "@/components/ThemeToggle";
import { VersionNuevaBanner } from "@/components/VersionNuevaBanner";
import { useAuth } from "@/hooks/useAuth";
import { usePendingApprovals } from "@/hooks/useApprovals";
import { useDashboards } from "@/hooks/useDashboards";
import { useLicenseStatus } from "@/hooks/useLicenseStatus";
import { useMyProfile } from "@/hooks/useMe";
import { useRecetasEstado } from "@/hooks/useRecetas";
import { useAuditoriaEstado } from "@/hooks/useAuditoriaPlaticas";
import { useSystemHealth } from "@/hooks/useSystemHealth";
import { cn } from "@/lib/utils";

function SidebarGroup({
  label,
  children,
  first = false,
}: {
  label: string;
  children: React.ReactNode;
  first?: boolean;
}) {
  const visibleChildren = React.Children.toArray(children).filter(Boolean);
  if (visibleChildren.length === 0) return null;

  return (
    <div className={first ? "" : "mt-4"}>
      <div className="text-[10px] font-medium tracking-[0.16em] text-muted-foreground uppercase mb-1 px-3">
        {label}
      </div>
      <div className="space-y-1">{visibleChildren}</div>
    </div>
  );
}

export function Layout() {
  const { user, logout } = useAuth();
  const healthQuery = useSystemHealth();
  const analysisEnabled = healthQuery.data?.analysis_enabled ?? false;
  const approvalsEnabled = healthQuery.data?.approvals_enabled ?? false;
  const approvalsQuery = usePendingApprovals();
  const pendingCount = approvalsQuery.data?.length ?? 0;
  const showApprovalsBadge = approvalsEnabled && pendingCount > 0;
  const licenseQuery = useLicenseStatus();
  const dashboardsQuery = useDashboards();
  const hasDashboards = (dashboardsQuery.data?.items?.length ?? 0) > 0;

  // PR 9 — permisos reales desde /me/profile
  const profileQuery = useMyProfile();
  const segments = new Set(profileQuery.data?.segments ?? []);
  const globalRoles = profileQuery.data?.global_roles ?? [];
  const isSuper = globalRoles.includes("super_user") || user?.role === "admin";
  const recetasEstado = useRecetasEstado();
  const auditoriaEstado = useAuditoriaEstado();

  // Fallback: mientras carga el profile, mostrar todo (evita parpadeo).
  const has = (code: string) => profileQuery.isPending || isSuper || segments.has(code);

  // Hub "Operación de respuesta automática": agrupa 5 vistas cuyas rutas no
  // comparten prefijo, así que el resaltado del ítem se calcula a mano.
  const location = useLocation();
  const HUB_PREFIXES = ["/incidents", "/analysis", "/approvals", "/runs", "/srp"];
  const hubActive =
    location.pathname === "/" ||
    HUB_PREFIXES.some((p) => location.pathname === p || location.pathname.startsWith(p + "/"));
  // Aparece si el usuario ve al menos una de sus vistas; aterriza en la primera.
  const hubVisible =
    has("dashboard") ||
    (has("analysis") && analysisEnabled) ||
    (has("approvals") && approvalsEnabled) ||
    has("runs") ||
    has("srp");
  const hubLanding = has("dashboard")
    ? "/"
    : has("analysis") && analysisEnabled
      ? "/analysis/new"
      : has("approvals") && approvalsEnabled
        ? "/approvals"
        : has("runs")
          ? "/runs"
          : "/srp/catalog";

  return (
    <div className="flex min-h-screen bg-muted/20">
      <aside className="flex w-60 shrink-0 flex-col border-r bg-card">
        <div className="flex items-center justify-between gap-2.5 px-1 pb-3 mb-2 border-b border-border">
          <div className="flex items-center gap-2.5">
            <AROCIcon size={38} />
            <div className="flex flex-col leading-tight">
              <div className="text-lg font-medium tracking-wide text-foreground">AROC</div>
              <div className="text-[9px] font-medium tracking-[0.16em] text-muted-foreground uppercase mt-0.5">Interfaz Operativa</div>
            </div>
          </div>
          <ThemeToggle />
        </div>
        <nav className="flex-1 overflow-y-auto p-3">
          <SidebarGroup label="OPERACION" first>
            {hubVisible && (
              <NavLink to={hubLanding} className={() => navClass(hubActive)}>
                <Zap className="size-4 shrink-0" />
                <span className="flex-1">Operación de respuesta automática</span>
                {showApprovalsBadge && (
                  <span className="rounded-full bg-amber-500 px-2 py-0.5 text-[10px] font-semibold text-white">
                    {pendingCount}
                  </span>
                )}
              </NavLink>
            )}
            {has("analysis") && analysisEnabled && (
              <NavLink to="/hablar" className={({ isActive }) => navClass(isActive)}>
                <MessageSquare className="size-4" /> Hablando con AROC
              </NavLink>
            )}
            {isSuper && recetasEstado.data?.enabled && (
              <NavLink to="/reportes-programados" className={({ isActive }) => navClass(isActive)}>
                <CalendarClock className="size-4" /> Reportes programados
              </NavLink>
            )}
            {isSuper && auditoriaEstado.data?.enabled && (
              <NavLink to="/auditoria-platicas" className={({ isActive }) => navClass(isActive)}>
                <ShieldCheck className="size-4" /> Auditoría de pláticas
              </NavLink>
            )}
            {has("correo") && (
              <NavLink to="/correo" className={({ isActive }) => navClass(isActive)}>
                <Mail className="size-4" /> Correo aroc@tilatina.com
              </NavLink>
            )}
            {has("health") && (
              <NavLink to="/health" className={({ isActive }) => navClass(isActive)}>
                <Activity className="size-4" /> Salud del sistema
              </NavLink>
            )}
            {has("carritos") && (
              <NavLink to="/holanda/carritos" className={({ isActive }) => navClass(isActive)}>
                <Snowflake className="size-4" /> Estacionamiento Holanda
              </NavLink>
            )}
            {has("zoom") && (
              <NavLink to="/zoom" className={({ isActive }) => navClass(isActive)}>
                <Phone className="size-4" /> Zoom tiLatina
              </NavLink>
            )}
            {has("admin_telegram") && (
              <NavLink to="/admin/telegram" className={({ isActive }) => navClass(isActive)}>
                <Send className="size-4" /> Telegram
              </NavLink>
            )}
          </SidebarGroup>

          <SidebarGroup label="REPORTERIA">
            {has("dashboards") && hasDashboards && (
              <NavLink to="/dashboards" className={({ isActive }) => navClass(isActive)}>
                <BarChart3 className="size-4" /> Dashboards
              </NavLink>
            )}
            {has("reports") && (
              <NavLink to="/reports" className={({ isActive }) => navClass(isActive)}>
                <FileText className="size-4" /> Reportes
              </NavLink>
            )}
            {has("ecc") && (
              <NavLink to="/ecc" className={({ isActive }) => navClass(isActive)}>
                <ClipboardCheck className="size-4" /> ECC
              </NavLink>
            )}
          </SidebarGroup>

          <SidebarGroup label="CONFIGURACION">
            {has("admin_config") && (
              <NavLink
                to="/admin/configuracion/control"
                className={({ isActive }) => navClass(isActive)}
              >
                <ListChecks className="size-4" /> Control de configuración
              </NavLink>
            )}
            {has("admin_config") && (
              <NavLink to="/admin/configuracion" end className={({ isActive }) => navClass(isActive)}>
                <ShieldCheck className="size-4" /> Auditoría de config
              </NavLink>
            )}
            {has("admin_config") && (
              <NavLink to="/admin/credenciales" className={({ isActive }) => navClass(isActive)}>
                <KeyRound className="size-4" /> Credenciales
              </NavLink>
            )}
            {has("admin_config") && (
              <NavLink
                to="/admin/configuracion/rollbacks"
                className={({ isActive }) => navClass(isActive)}
              >
                <ShieldCheck className="size-4" /> Historial de rollbacks
              </NavLink>
            )}
          </SidebarGroup>

          <SidebarGroup label="CUENTA">
            {isSuper && (
              <NavLink to="/admin/users" className={({ isActive }) => navClass(isActive)}>
                <Users className="size-4" /> Usuarios
              </NavLink>
            )}
            {has("usage") && (
              <NavLink to="/usage" className={({ isActive }) => navClass(isActive)}>
                <Gauge className="size-4" /> Mi uso
              </NavLink>
            )}
            <NavLink to="/settings/profile" className={({ isActive }) => navClass(isActive)}>
              <Settings className="size-4" /> Mi perfil
            </NavLink>
            {has("admin_usage") && (
              <NavLink to="/admin/usage" className={({ isActive }) => navClass(isActive)}>
                <Coins className="size-4" /> Uso global
              </NavLink>
            )}
            {isSuper && (
              <NavLink to="/admin/segments" className={({ isActive }) => navClass(isActive)}>
                <LayoutGrid className="size-4" /> Segmentos
              </NavLink>
            )}
          </SidebarGroup>

          <SidebarGroup label="AUDITORIA">
            {isSuper && (
              <NavLink to="/admin/audit-log" className={({ isActive }) => navClass(isActive)}>
                <History className="size-4" /> Audit Log
              </NavLink>
            )}
          </SidebarGroup>
        </nav>
        <div className="border-t p-3 text-sm">
          <div className="px-3 py-2">
            <div className="font-medium">{user?.username ?? "—"}</div>
            <div className="text-xs text-muted-foreground">
              {globalRoles.length > 0 ? globalRoles.join(", ") : (user?.role ?? "")}
            </div>
          </div>
          <Button
            variant="ghost"
            className="w-full justify-start text-muted-foreground"
            onClick={() => void logout()}
          >
            <LogOut className="size-4" /> Cerrar sesión
          </Button>
        </div>
      </aside>
      <main className="flex-1 overflow-auto p-8">
        <VersionNuevaBanner />
        <div className="mb-4">
          <LicenseBanner status={licenseQuery.data} />
        </div>
        <Outlet />
      </main>
    </div>
  );
}

function navClass(isActive: boolean): string {
  return cn(
    "flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors",
    isActive
      ? "bg-primary text-primary-foreground"
      : "text-muted-foreground hover:bg-accent hover:text-accent-foreground",
  );
}
